/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, Text, Toast } from '@chakra-ui/react';
import { ArrowBackIcon, AddIcon } from '@chakra-ui/icons';
import ReusableTable from 'components/ReusableTable';
import SystemAxisWeightForm from './systemAxisWeightForm';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSystemAxisList } from 'api/auth.api';
import { useToast } from '@chakra-ui/react';
import useDebounce from 'constants/helper/useDebounce';

function SystemAxisWeight() {
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(1);
  const [searchString, setSearchString] = useState('');
  const debouncedSearchTerm = useDebounce(searchString, 1000, setPage);

  const [selectedId, setSelectedId] = useState(null);

  const toast = useToast();
  const queryClient = useQueryClient();

  const payload = {
    searchString: debouncedSearchTerm,
    page,
  };

  const { data, isFetching, error, isSuccess, isError } = useQuery({
    queryKey: ['systemAxisList', debouncedSearchTerm, page],
    queryFn: () => getSystemAxisList(payload),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (isError) {
      toast({
        title: 'Failed to fetch system axis list',
        description: error?.message || 'Something went wrong',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-center',
      });
    }
  }, [isError, error, toast]);

  const tableData = data?.data?.result?.map((item, index) => ({
    ...item,
    srNo: (page - 1) * 20 + index + 1, // 10 = pageSize
  }));

  return (
    <Box p={4} mt={10}>
      {!showForm && (
        <ReusableTable
          data={tableData}
          columns={[
            { key: 'srNo', label: 'Sr.no', width: '80px', headerSize: '14px' },
            {
              key: 'name',
              label: 'System',
              width: '200px',
              headerSize: '14px',
            },
            {
              key: 'axisCode',
              label: 'Axis Code',
              width: '140px',
              headerSize: '14px',
            },
            {
              key: 'weightPriority',
              label: 'Weight Priority',
              width: '140px',
              headerSize: '14px',
            },
            {
              key: 'defaultElectricity',
              label: 'Default Electricity',
              width: '140px',
              headerSize: '14px',
            },
            {
              key: 'defaultPolarity',
              label: 'Default Polarity',
              width: '80px',
              headerSize: '14px',
            },
            {
              key: 'appArea',
              label: 'App Area',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'temperament',
              label: 'Constitution',
              width: '140px',
              headerSize: '14px',
            },
            {
              key: 'note',
              label: 'Note',
              width: '300px',
              headerSize: '14px',
            },
          ]}
          page={page}
          totalPages={data?.data?.totalPages || 1}
          loading={false}
          onRowClick={() => {}}
          onEdit={(id) => {
            setSelectedId(id);
            setShowForm(true);
          }}
          onPageChange={(newPage) => setPage(newPage)}
          showTopAction={true}
          showSearch={true}
          onSearch={(value) => {
            setSearchString(value);
            setPage(1);
          }}
          topAction={{
            title: 'System Axis Weight Page',
            label: 'Add Weight',
            icon: <AddIcon />,
            onClick: () => setShowForm(true),
          }}
          showViewToggle={false}
        />
      )}

      {showForm && (
        <Box w="100%">
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            gap={4}
            mb={4}
            ml={3}
          >
            <Text fontSize="22px" fontWeight="bold">
              System Axis Weight Form
            </Text>
            <Button
              variant="ghost"
              leftIcon={<ArrowBackIcon />}
              onClick={() => {
                setShowForm(false);
                setSelectedId(null);
              }}
            >
              Back
            </Button>
          </Flex>

          <SystemAxisWeightForm
            editId={selectedId}
            onSuccess={() => {
              queryClient.invalidateQueries(['systemAxisList']);
              setShowForm(false);
            }}
          />
        </Box>
      )}
    </Box>
  );
}

export default SystemAxisWeight;
